/**
 * Outbound links use VITE_* (inlined at build time).
 *
 * Production split:
 * - Marketing (this site): kynward.com — legal, homepage, waitlist
 * - App (Expo web): app.kynward.com — signup / product
 */
import { hasHeroClusterRaster } from './assets/marketing/hero/heroClusterExport';

const DEFAULT_MARKETING_ORIGIN = 'https://kynward.com';
const DEFAULT_APP_ORIGIN = 'https://app.kynward.com';

function stripTrailingSlash(s: string) {
  return s.replace(/\/+$/, '');
}

function appWebOriginFromEnv(): string {
  const env = import.meta.env as Record<string, string | undefined>;
  return (
    env.VITE_KYNWARD_APP_WEB_ORIGIN?.trim() || env.VITE_HEARTHFOLD_APP_WEB_ORIGIN?.trim() || ''
  );
}

const appOrigin = stripTrailingSlash(appWebOriginFromEnv() || DEFAULT_APP_ORIGIN);

/** Canonical origin where this marketing build is hosted (legal absolute URLs, etc.). */
export function siteOrigin(): string {
  const raw = (import.meta.env.VITE_MARKETING_SITE_ORIGIN as string | undefined)?.trim();
  return stripTrailingSlash(raw || DEFAULT_MARKETING_ORIGIN);
}

function at(path: string) {
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${appOrigin}${p}`;
}

const explicit = (key: string) =>
  (import.meta.env as Record<string, string | undefined>)[key]?.trim() || '';

/** `text_heavy_control`: variant-based hero aside. `social_visuals_v1`: hero raster or SVG scene. */
export type MarketingVisualVariant = 'text_heavy_control' | 'social_visuals_v1';

export function marketingVisualVariant(): MarketingVisualVariant {
  const v = explicit('VITE_MARKETING_VISUAL_VARIANT');
  if (v === 'social_visuals_v1') return 'social_visuals_v1';
  if (v === 'text_heavy_control') return 'text_heavy_control';
  return hasHeroClusterRaster() ? 'social_visuals_v1' : 'text_heavy_control';
}

const env = () => import.meta.env as Record<string, string | undefined>;

/**
 * Bare hostnames (e.g. `api.dev.kynward.app`) resolve as paths on the marketing
 * origin (`https://kynward.com/api.dev.kynward.app/...`) — always return an absolute origin.
 */
function isDisallowedMarketingBackendHost(hostname: string): boolean {
  const h = hostname.toLowerCase();
  if (h === 'storage.googleapis.com' || h.endsWith('.storage.googleapis.com')) {
    return true;
  }
  // Bucket website / XML API endpoints — not the JSON API.
  if (h.endsWith('.storage-client.googleapis.com')) return true;
  return false;
}

export function normalizeMarketingBackendUrl(raw: string): string {
  let trimmed = stripTrailingSlash(raw.trim());
  if (!trimmed) return '';
  if (trimmed.startsWith('//')) trimmed = `https:${trimmed}`;
  if (!/^https?:\/\//i.test(trimmed)) {
    trimmed = trimmed.replace(/^\/+/, '');
    if (!trimmed || trimmed.includes('/')) return '';
    trimmed = `https://${trimmed}`;
  }
  try {
    const { origin, hostname } = new URL(trimmed);
    if (isDisallowedMarketingBackendHost(hostname)) return '';
    return stripTrailingSlash(origin);
  } catch {
    return '';
  }
}

/** Absolute API URL on the marketing backend (path must start with `/`). */
export function marketingBackendApiUrl(path: string): string {
  const base = marketingBackendUrl();
  if (!base) return '';
  const p = path.startsWith('/') ? path : `/${path}`;
  return new URL(p, `${base}/`).href;
}

/**
 * Kynward **backend API** origin for the marketing site (no path).
 * Waitlist POSTs to `${marketingBackendUrl()}/api/waitlist/full-release`.
 *
 * Set **`VITE_MARKETING_BACKEND_URL`** at build time (preferred), e.g.:
 * - Production: `https://api.kynward.com`
 * - Local API: `http://localhost:8080`
 *
 * **`VITE_API_BASE`** is still read if the new name is unset (legacy CI/env files).
 */
export function marketingBackendUrl(): string {
  const raw = env().VITE_MARKETING_BACKEND_URL?.trim() || env().VITE_API_BASE?.trim() || '';
  return normalizeMarketingBackendUrl(raw);
}

/** @deprecated Prefer {@link marketingBackendUrl} */
export function apiBase(): string {
  return marketingBackendUrl();
}

/**
 * App Store / Play badges and “Download” CTAs. Off until launch — set
 * `VITE_MARKETING_SHOW_APP_DOWNLOAD=1` at build time to enable.
 */
export function marketingAppDownloadVisible(): boolean {
  return import.meta.env.VITE_MARKETING_SHOW_APP_DOWNLOAD === '1';
}

export const links = {
  appOrigin,
  /** Invite / sign in doorway */
  signup: explicit('VITE_LINK_SIGNUP') || at('/signup'),
  /** Privacy — served by this marketing site unless overridden */
  privacy: explicit('VITE_PRIVACY_URL') || '/legal/privacy-policy',
  /** Terms — served by this marketing site unless overridden */
  terms: explicit('VITE_TERMS_URL') || '/legal/terms-of-service',
  supportEmail: explicit('VITE_SUPPORT_EMAIL') || 'support@kynward.com',
  ios: explicit('VITE_IOS_APP_STORE_URL') || '',
  android:
    explicit('VITE_ANDROID_PLAY_STORE_URL') ||
    'https://play.google.com/store/apps/details?id=com.kynward.app',
} as const;
